import { Spinner } from '@/shared/components/ui/Spinner';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { cn, timeAgo } from '@/shared/utils/utils';
import { resolveAction, entityLabel } from '../constants';
import type { ActivityItem } from '../types';

interface ActivityTimelineProps {
  items: ActivityItem[];
  isLoading?: boolean;
  isError?: boolean;
  /** Show the actor's name (global feed). Hide for own/entity timelines. */
  showActor?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
}

const TONE_BG: Record<string, string> = {
  neutral: 'bg-surface-variant text-on-surface-variant',
  primary: 'bg-primary-container text-on-primary-container',
  info: 'bg-info/10 text-info',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  danger: 'bg-danger/10 text-danger',
};

/**
 * Reusable, presentation-only activity timeline. Used by the global Activity
 * page and by entity drawers (Lead, Deal). Data fetching lives in hooks.
 * Items are expected in reverse-chronological order (backend default).
 */
export function ActivityTimeline({
  items,
  isLoading,
  isError,
  showActor = true,
  emptyTitle = 'No activity yet',
  emptyDescription = 'Activity will appear here as actions are performed.',
  className,
}: ActivityTimelineProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return <EmptyState title="Failed to load activity" description="Check your connection or try refreshing." />;
  }
  if (!items.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <ol className={cn('relative flex flex-col', className)}>
      {items.map((item, idx) => {
        const meta = resolveAction(item.action);
        const Icon = meta.icon;
        const isLast = idx === items.length - 1;
        return (
          <li key={item.id} className="flex gap-3">
            {/* Rail */}
            <div className="flex flex-col items-center">
              <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', TONE_BG[meta.tone])}>
                <Icon className="h-4 w-4" />
              </span>
              {!isLast && <span className="w-px flex-1 bg-outline" />}
            </div>
            {/* Body */}
            <div className={cn('min-w-0 pb-5', isLast && 'pb-0')}>
              <p className="flex flex-wrap items-center gap-2 text-data-md text-on-surface">
                <span>
                  {showActor && item.actor && <span className="font-medium">{item.actor} </span>}
                  <span className="text-on-surface-variant">{meta.label}</span>
                </span>
                {entityLabel(item.entity_type) && (
                  <Badge tone="neutral">{entityLabel(item.entity_type)}</Badge>
                )}
              </p>
              {item.detail && <p className="mt-0.5 break-words text-body-sm text-on-surface-variant">{item.detail}</p>}
              <p className="mt-0.5 text-label-caps uppercase text-on-surface-variant/70">{timeAgo(item.created_at)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
